/**
 * Publishing the manual book to the chain — ROADMAP 9.11.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A SECOND READER OF ONE READ, NOT A SECOND READ
 *
 * The chain needs to know what each strike holds, and the open book is
 * already fetched — by `useOpenBook` inside `BookDrawer`, which is MOUNTED
 * for the whole session (`Terminal.tsx` passes `open` as a prop rather than
 * conditionally rendering it), so its data is there whether the drawer is
 * showing or not.
 *
 * Calling `useOpenBook` a second time from the chain would double every
 * `GET /trades/open` and give the two copies different refresh moments, so
 * the same strike could be marked in one place and not the other. Hoisting
 * the hook into `Terminal` instead would move ownership of the drawer's own
 * data out of the drawer, across a screen that is already over its line
 * budget.
 *
 * So the drawer keeps the read and PUBLISHES the derived map here. One owner,
 * one fetch, one moment of truth, and the chain subscribes to a slice.
 *
 * DERIVED STATE, NEVER AUTHORITATIVE. This is a cache of a projection of a
 * projection: `Fill` -> `TradeLeg.netQty` -> the open book -> this. It is
 * rebuilt wholesale on every publish and never patched, it is not persisted,
 * and after a reload it is empty until the first read lands — at which point
 * the chain shows no badges, which is the honest answer to "we have not
 * looked yet" rather than a stale one.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useSyncExternalStore } from 'react'
import {
  manualHeldFrom,
  totalHeldFrom,
  heldKey,
  type HeldContract,
  type ManualHeld,
  type TotalHeld,
} from './manualHeld'
import type { OpenBookTrade } from './useOpenBook'

type Listener = () => void

const EMPTY: ManualHeld = new Map()
const EMPTY_TOTAL: TotalHeld = new Map()

class HeldStore {
  private readonly listeners = new Set<Listener>()
  private held: ManualHeld = EMPTY
  /** 9.27. Everything, anywhere. A different question — see `totalHeldFrom`. */
  private total: TotalHeld = EMPTY_TOTAL

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /**
   * Identity-stable between publishes.
   *
   * `useSyncExternalStore` compares snapshots by reference and re-renders
   * forever if a fresh object comes back on every call.
   */
  getSnapshot = (): ManualHeld => this.held
  getTotalSnapshot = (): TotalHeld => this.total

  /** Rebuild from the book. Called by whoever owns the read. */
  publish(trades: readonly OpenBookTrade[]): void {
    const next = manualHeldFrom(trades)
    const nextTotal = totalHeldFrom(trades)
    /*
     * Skip the notify when nothing a chain row would draw has changed. A
     * fill arrives on every tick of a working basket and the chain is 460
     * cells; re-rendering all of them because an unrelated leg's P&L moved
     * is the kind of cost that only shows up on a slow machine in a fast
     * market.
     */
    if (same(this.held, next) && sameTotal(this.total, nextTotal)) return
    this.held = next
    this.total = nextTotal
    for (const fn of this.listeners) fn()
  }

  /** On logout, or when the read fails. Nothing held is better than stale. */
  clear(): void {
    if (this.held.size === 0 && this.total.size === 0) return
    this.held = EMPTY
    this.total = EMPTY_TOTAL
    for (const fn of this.listeners) fn()
  }
}

const sameTotal = (a: TotalHeld, b: TotalHeld): boolean => {
  if (a.size !== b.size) return false
  for (const [k, v] of a) {
    const other = b.get(k)
    if (other === undefined || other.units !== v.units || other.lotSize !== v.lotSize) return false
  }
  return true
}

const same = (a: ManualHeld, b: ManualHeld): boolean => {
  if (a.size !== b.size) return false
  for (const [k, v] of a) {
    const other = b.get(k)
    if (other === undefined || other.units !== v.units || other.lotSize !== v.lotSize) return false
  }
  return true
}

export const heldStore = new HeldStore()

/**
 * What one contract holds in the manual block of one account.
 *
 * `connectionId` is the account the NEXT order would go to — the header's
 * selector — because that is the only account a click on this chain can
 * reduce. A position on the same strike in another account is real and is
 * deliberately not shown here: marking it would promise a reduce that
 * `manualNetting` refuses to make across accounts.
 */
export function useManualHeld(connectionId: string | null, token: number | null): HeldContract | null {
  const held = useSyncExternalStore(heldStore.subscribe, heldStore.getSnapshot, heldStore.getSnapshot)
  if (connectionId === null || token === null) return null
  return held.get(heldKey(connectionId, token)) ?? null
}


/**
 * EVERYTHING held on one contract, across every account and strategy — 9.27.
 *
 * No `connectionId`, deliberately: this is exposure rather than netting
 * eligibility, and exposure does not stop at an account boundary. Pair it
 * with `useManualHeld` where the chip has to say which of the two it means.
 */
export function useTotalHeld(token: number | null): HeldContract | null {
  const total = useSyncExternalStore(
    heldStore.subscribe,
    heldStore.getTotalSnapshot,
    heldStore.getTotalSnapshot,
  )
  if (token === null) return null
  return total.get(token) ?? null
}
